/**
 * The tenant's emergency SOS state (ADR-0029), shared by the red banner every
 * screen shows while SOS is on and by the SOS screen itself, so switching it
 * on or off in one place updates the other at once.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api.js';
import { useAuth } from './auth.js';
import type { SosState } from './types.js';

/** Someone else may switch SOS on; every open console should notice soon. */
const POLL_MS = 15000;

interface SosContextValue {
  sos: SosState | null;
  /** Replace the shared state with a server response (after a mutation). */
  setSos: (next: SosState) => void;
  refresh: () => void;
}

const SosContext = createContext<SosContextValue>({ sos: null, setSos: () => {}, refresh: () => {} });

export function SosProvider({ children }: { children: ReactNode }): ReactNode {
  const { can } = useAuth();
  const allowed = can('sos.view');
  const [sos, setSos] = useState<SosState | null>(null);

  const refresh = useCallback(() => {
    if (!allowed) return;
    void api
      .sos()
      .then(setSos)
      .catch(() => {});
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [allowed, refresh]);

  return <SosContext.Provider value={{ sos, setSos, refresh }}>{children}</SosContext.Provider>;
}

export function useSos(): SosContextValue {
  return useContext(SosContext);
}
